/**
 * runPendingSave — the save() pipeline extracted out of
 * usePendingChannelDrops to keep the hook file under the 200-line FE limit.
 *
 * Pipeline (post 2026-05-21 user-bug fix):
 *   1. runDeletes → deleteChannelImage per queued delete.
 *   2. runChannelInsertsAppend → for each pending CHANNEL drop:
 *      transfer → linkAssetToChannel at TEMP slot = maxExistingSlot + 1 + i
 *      (avoids the upsert path that would otherwise REPLACE the occupant).
 *   3. runChannelReorderFinal → reorderProductImages once per affected
 *      channel with the FULL final link-id list (existing + new inserts).
 *   4. runAiRefInserts → single transferFromGalleryToProduct call per
 *      pending ai_reference drop (untouched legacy path).
 *   5. finalize → invalidate queries + toast.
 *
 * Why this order: backend `reorder_channel_images` stamps slots 1..N from the
 * input list, so the reorder MUST know the new link-ids — hence inserts run
 * first. See runPendingSaveChannels.ts for the details.
 */
import type { Dispatch, SetStateAction } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18n from '@/i18n'
import { transferFromGalleryToProduct } from '@/lib/api/product-images'
import { deleteChannelImage } from '@/lib/api/products'
import { invalidateProductSlots } from './applyTransfer'
import type { PendingShift } from './buildReorderPlan'
import {
  runChannelInsertsAppend,
  runChannelReorderFinal,
} from './runPendingSaveChannels'
import { runChannelPlans } from './runPendingSavePlans'
import type { ChannelReorderPlans } from './channelReorderPlan'
import type { AppliedDelete } from './applyDelete'
import type { AddToProductImage, AddToProductTarget } from '../types'
import type { CurrentSlotsByChannel, PendingDrop } from './usePendingChannelDrops'

export interface SaveCtx {
  productId: string
  pending: Map<string, PendingDrop>
  shifts: PendingShift[]
  deletes: AppliedDelete[]
  snapshot: CurrentSlotsByChannel
  plans: ChannelReorderPlans
  queryClient: QueryClient
  setSaving: Dispatch<SetStateAction<boolean>>
  clear: () => void
  onSaved?: () => void
}

function hasPlanWork(plans: ChannelReorderPlans): boolean {
  for (const plan of plans.values()) if (plan.length > 0) return true
  return false
}

function shouldSkipSave(ctx: SaveCtx): boolean {
  if (!ctx.productId) return true
  return (
    ctx.pending.size === 0 &&
    ctx.shifts.length === 0 &&
    ctx.deletes.length === 0 &&
    !hasPlanWork(ctx.plans)
  )
}

function filterPendingByChannels(
  pending: Map<string, PendingDrop>,
  excludeChannels: Set<string>,
): Map<string, PendingDrop> {
  const out = new Map<string, PendingDrop>()
  for (const [k, d] of pending) {
    if (d.target.type === 'channel' && excludeChannels.has(d.target.channelId)) continue
    out.set(k, d)
  }
  return out
}

export async function runSave(ctx: SaveCtx): Promise<boolean> {
  if (shouldSkipSave(ctx)) return true
  ctx.setSaving(true)
  try {
    await runDeletes(ctx)
    // Live-fix: per-channel ReorderPlan path runs FIRST and claims those
    // channels for the duration of this save — legacy shifts/inserts are
    // skipped for those channels to avoid double-writes.
    const planRes = await runChannelPlans({ productId: ctx.productId, plans: ctx.plans })
    const legacyPending = filterPendingByChannels(ctx.pending, planRes.handledChannels)
    const legacyShifts = ctx.shifts.filter(
      (s) => !planRes.handledChannels.has(s.channelId),
    )
    const channelRes = await runChannelInsertsAppend({
      productId: ctx.productId,
      pending: legacyPending,
      snapshot: ctx.snapshot,
    })
    await runChannelReorderFinal(
      {
        productId: ctx.productId,
        pending: legacyPending,
        shifts: legacyShifts,
        snapshot: ctx.snapshot,
      },
      channelRes.newLinkIds,
    )
    const aiRes = await runAiRefInserts(ctx)
    const totalErr = planRes.err + channelRes.err + aiRes.err
    finalize(
      ctx,
      planRes.ok + channelRes.ok + aiRes.ok,
      totalErr,
      planRes.handledChannels.size > 0,
    )
    return totalErr === 0
  } finally {
    ctx.setSaving(false)
  }
}

async function runDeletes(ctx: SaveCtx): Promise<void> {
  for (const d of ctx.deletes) {
    const t = d.target
    if (t.type === 'channel') {
      try {
        await deleteChannelImage(ctx.productId, t.channelId, t.slot)
      } catch {
        // swallow — toast surfaces overall errors via finalize
      }
    }
  }
}

async function runAiRefInserts(ctx: SaveCtx): Promise<{ ok: number; err: number }> {
  let ok = 0
  let err = 0
  for (const { image, target } of ctx.pending.values()) {
    if (target.type !== 'ai_reference') continue
    const success = await commitAiRef(image, target, ctx.productId)
    if (success) ok++
    else err++
  }
  return { ok, err }
}

function finalize(
  ctx: SaveCtx,
  ok: number,
  err: number,
  hadPlanWork: boolean = false,
): void {
  const anyWork =
    ok > 0 || ctx.shifts.length > 0 || ctx.deletes.length > 0 || hadPlanWork
  if (anyWork) {
    invalidateProductSlots(ctx.queryClient, ctx.productId)
    if (ok > 0) toast.success(i18n.t('addToProduct.media.saveSuccess', { count: ok }))
    ctx.clear()
    ctx.onSaved?.()
  }
  if (err > 0) {
    toast.error(i18n.t('addToProduct.media.saveError', { count: err }))
  }
}

async function commitAiRef(
  image: AddToProductImage,
  target: AddToProductTarget & { type: 'ai_reference' },
  productId: string,
): Promise<boolean> {
  try {
    const res = await transferFromGalleryToProduct(image.id, productId, {
      targetType: 'ai_reference',
      slot: target.slot,
    })
    return !!(res?.success || res?.already_transferred)
  } catch {
    return false
  }
}
